import maplibregl, { Map as MlMap } from "maplibre-gl";
import mapStyle from "./styles/plague-map-style.json";
import { applyI18n, detectLocale, setLocale, t, type Locale } from "./i18n/index.js";
import { fetchDates, fetchIncidents, type Incident, type DateRow } from "./api.js";

// ────────────────────────────────────────────────────────────────────
// State
// ────────────────────────────────────────────────────────────────────
type State = {
  locale: Locale;
  allIncidents: Incident[];      // every incident the API knows about
  visibleIncidents: Incident[];  // after date + filters
  dates: DateRow[];
  selectedDate: string;          // YYYY-MM-DD
  selectedIncidentId: string | null;
  filters: {
    strains: Set<string>;        // empty = all
    vectors: { cruise_ship: boolean; plane: boolean; none: boolean };
    countries: Set<string>;      // empty = all
  };
  playing: boolean;
  speed: 1 | 2 | 4;
  /** Milliseconds per day at 1× speed. 9 000 ms (9 s) is slow enough for the
   *  daily ticker to sweep its longest item before the next day fires. */
  PLAY_BASE_MS: number;
};

const state: State = {
  locale: detectLocale(),
  allIncidents: [],
  visibleIncidents: [],
  dates: [],
  selectedDate: "",
  selectedIncidentId: null,
  filters: {
    strains: new Set(),
    vectors: { cruise_ship: true, plane: true, none: true },
    countries: new Set(),
  },
  playing: false,
  speed: 1,
  PLAY_BASE_MS: 9000,
};

// ────────────────────────────────────────────────────────────────────
// Map
// ────────────────────────────────────────────────────────────────────
const map = new MlMap({
  container: "map",
  style: mapStyle as maplibregl.StyleSpecification,
  center: [10, 35],
  zoom: 1.8,
  minZoom: 1.2,
  // maxZoom 9 lets the user inspect specific airports / hospitals (LUMC,
  // Radboud, AMC, ZRH, OR Tambo, etc.). Markers are GPU-rendered vector
  // features now, so they stay glued at any zoom level.
  maxZoom: 9,
  attributionControl: { compact: true },
  renderWorldCopies: true,
  doubleClickZoom: true,
});

map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");

// Expose for debugging + smoke tests. Read-only data, no security concern.
(window as unknown as { __map: typeof map }).__map = map;

/** Single active popup — opening a new one closes the previous, so clicking
 *  through a stack of markers (e.g. JNB has the Airlink flight + KL592 +
 *  the deceased wife) doesn't pile multiple popups on top of each other. */
let activePopup: maplibregl.Popup | null = null;
function openPopup(lngLat: maplibregl.LngLatLike, html: string): maplibregl.Popup {
  activePopup?.remove();
  activePopup = new maplibregl.Popup({ closeButton: true, maxWidth: "300px" })
    .setLngLat(lngLat)
    .setHTML(html)
    .addTo(map);
  return activePopup;
}

/** Recentre / fit the map to the bounding box of currently-visible incidents.
 *  Called by the "FIT ALL" button and by the wordmark click. Falls back to a
 *  world view if there are no markers. */
function fitToVisible(): void {
  if (!state.visibleIncidents.length) {
    map.flyTo({ center: [10, 35], zoom: 1.8, speed: 1.2 });
    return;
  }
  const bounds = new maplibregl.LngLatBounds();
  for (const i of state.visibleIncidents) {
    // Use the live (date-aware) position so that scrubbing back to Day 1
    // frames the ship at Ushuaia rather than at its current Cape Verde location.
    const [lon, lat] = liveCoords(i, state.selectedDate);
    bounds.extend([lon, lat]);
    // Include the visible route history so the line is fully in frame.
    const log = parseRouteLog(i);
    for (const w of log) {
      if (w.date <= state.selectedDate) bounds.extend([w.lon, w.lat]);
    }
    // Include every visible confirmed-case dot — otherwise a patient who
    // travelled far (e.g. the Swiss patient flown to Zurich) drops out of frame.
    for (const p of parsePersons(i)) {
      const events = (p.events ?? []).filter((e) => e.date <= state.selectedDate);
      const cur = events[events.length - 1];
      if (cur) bounds.extend([cur.lon, cur.lat]);
    }
  }
  map.fitBounds(bounds, {
    padding: { top: 100, bottom: 140, left: 320, right: 80 },
    maxZoom: 4,
    duration: 700,
  });
}

// Boat / plane SVGs — Material Design Icons ferry + airplane (MIT).
// Used both in the right detail panel and as map symbol layer images.
const BOAT_SVG = `<svg viewBox="0 0 24 24"><path d="M6 6h12v3.96L12 8L6 9.96M3.94 19H4c1.6 0 3-.88 4-2c1 1.12 2.4 2 4 2s3-.88 4-2c1 1.12 2.4 2 4 2h.05l1.9-6.69c.08-.25.05-.53-.06-.77c-.13-.24-.34-.42-.6-.5L20 10.62V6a2 2 0 0 0-2-2h-3V1H9v3H6a2 2 0 0 0-2 2v4.62l-1.29.42c-.26.08-.47.26-.6.5c-.11.24-.14.52-.06.77M20 21c-1.39 0-2.78-.47-4-1.33c-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.37 0 2.74-.35 4-1c2.5 1.3 5.5 1.3 8 0c1.26.65 2.62 1 4 1h2v-2z"/></svg>`;
const PLANE_SVG = `<svg viewBox="0 0 24 24"><path d="M20.56 3.91c.59.59.59 1.54 0 2.12l-3.89 3.89l2.12 9.19l-1.41 1.42l-3.88-7.43L9.6 17l.36 2.47l-1.07 1.06l-1.76-3.18l-3.19-1.77L5 14.5l2.5.37L11.37 11L3.94 7.09l1.42-1.41l9.19 2.12l3.89-3.89c.56-.58 1.56-.58 2.12 0"/></svg>`;

// Sources rendered into the MapLibre style. Doing this once and updating .data
// avoids the sub-pixel marker drift that DOM Markers exhibit during zoom — the
// GPU rasterises circle/symbol layers at exact projected coordinates every
// frame, so they stay glued to the geography no matter how aggressively you
// scroll-zoom or pinch.
const SOURCE_INCIDENTS = "incidents";
const SOURCE_TRAJECTORIES = "trajectories";
const SOURCE_ROUTE_PINS = "route-pins";
const SOURCE_PERSONS = "persons";

type Person = {
  id: string;
  label_en: string;
  label_nl: string;
  nationality: string;
  events: Array<{
    date: string;
    lat: number;
    lon: number;
    location: string;
    location_nl?: string;
    country_code?: string;
    /**
     * Person status drives the dot colour:
     *   - 'suspected' → yellow (under investigation, awaiting lab result)
     *   - 'ill'       → red (lab-confirmed alive)
     *   - 'deceased'  → black with red outline
     *   - 'cleared'   → muted grey (was suspected, lab returned negative)
     */
    status: "suspected" | "ill" | "deceased" | "cleared";
    note_en?: string;
    note_nl?: string;
  }>;
};

type CountryRef = { code: string; label_en: string; label_nl: string };

function parsePersons(inc: Incident): Person[] {
  if (!inc.persons) return [];
  try {
    const arr = JSON.parse(inc.persons) as Person[];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function parseCountriesVisited(inc: Incident): CountryRef[] {
  if (!inc.countries_visited) return [];
  try {
    const arr = JSON.parse(inc.countries_visited) as CountryRef[];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

type RouteWaypoint = {
  date: string;
  lat: number;
  lon: number;
  location: string;
  location_nl?: string;
  note_en?: string;
  note_nl?: string;
  /** Optional delta applied to the cumulative case count on this date —
   *  e.g. { deceased: 1 } when a passenger dies that day. Lets the topbar
   *  counters rise day-by-day instead of jumping to the final total at the
   *  incident's reported_at. */
  delta?: { detected?: number; deceased?: number };
};

/** Pick the locale-appropriate variant. Returns the NL field in NL mode
 *  (when present) and the EN field everywhere else, with a final fallback to
 *  whichever is non-empty. */
function loc(locale: "en" | "nl", en: string | null | undefined, nl: string | null | undefined): string {
  if (locale === "nl" && nl) return nl;
  if (locale === "en" && en) return en;
  return (locale === "nl" ? (nl ?? en) : (en ?? nl)) ?? "";
}

function emptyFc(): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
  return { type: "FeatureCollection", features: [] };
}

/** Decode an incident's route_log JSON; tolerant of nulls / parse errors. */
function parseRouteLog(inc: Incident): RouteWaypoint[] {
  if (!inc.route_log) return [];
  try {
    const arr = JSON.parse(inc.route_log) as RouteWaypoint[];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

/** Smoothing factor in [0,1] applied during playback — when the day-clock is
 *  half-way to the next day, vessel positions are linearly interpolated 50 %
 *  between today's waypoint and tomorrow's so the boat slides along the line
 *  rather than teleporting. Refreshed every animation frame by the play loop;
 *  always 0 when paused. */
let interp = 0;

/** Where is the vessel right now (i.e. on `state.selectedDate`)?
 *  Returns the latest route_log waypoint dated ≤ selectedDate; if a non-zero
 *  interp factor is present (during playback) it linearly blends toward the
 *  next-day waypoint, so the boat slides smoothly. Falls back to the
 *  incident's static lat/lon when no route_log is present. */
function liveCoords(inc: Incident, selectedDate: string): [number, number] {
  const log = parseRouteLog(inc);
  if (!log.length) return [inc.lon, inc.lat];
  let latestIdx = -1;
  for (let i = 0; i < log.length; i++) {
    if (log[i]!.date <= selectedDate) latestIdx = i;
  }
  if (latestIdx < 0) return [log[0]!.lon, log[0]!.lat];
  const cur = log[latestIdx]!;
  const next = latestIdx + 1 < log.length ? log[latestIdx + 1]! : null;
  if (!next || interp <= 0) return [cur.lon, cur.lat];
  // Linear interpolation in lon/lat — accurate enough for the 1-day spans we
  // animate (a great-circle would buy little at this scale).
  const k = Math.min(1, interp);
  return [cur.lon + (next.lon - cur.lon) * k, cur.lat + (next.lat - cur.lat) * k];
}

function buildIncidentFeatureCollection(rows: Incident[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: "FeatureCollection",
    features: rows.map((i) => {
      const [lon, lat] = liveCoords(i, state.selectedDate);
      const tot = dynamicTotals(i, state.selectedDate);
      const liveCount = tot.deceased + tot.detected;
      const locName = loc(state.locale, i.location_name, i.location_name_nl);
      return {
        type: "Feature" as const,
        id: i.id,
        geometry: { type: "Point" as const, coordinates: [lon, lat] },
        properties: {
          id: i.id,
          case_type: i.case_type,
          case_count: liveCount,
          vector: i.vector ?? "",
          flight_type: i.flight_type ?? (i.vector === "plane" ? "passenger" : ""),
          title: `${locName} — ${liveCount} ${i.case_type}`,
        },
      };
    }),
  };
}

/** Trajectory line — only segments up to `selectedDate`, so the route reveals
 *  itself day-by-day as the scrubber advances. Prefers route_log (one segment
 *  per day = visually smooth) over the sparse trajectory[] field. */
function buildTrajectoryFeatureCollection(rows: Incident[], selectedDate: string): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  const out: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  for (const i of rows) {
    let pts: Array<[number, number]> = [];
    const log = parseRouteLog(i);
    if (log.length >= 2) {
      pts = log.filter((w) => w.date <= selectedDate).map((w) => [w.lon, w.lat]);
    } else if (i.trajectory) {
      try {
        const parsed = JSON.parse(i.trajectory) as Array<[number, number]>;
        if (Array.isArray(parsed)) pts = parsed;
      } catch { /* ignore */ }
    }
    if (pts.length < 2) continue;
    out.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: pts },
      properties: {
        id: i.id,
        vector: i.vector ?? "",
        flight_type: i.flight_type ?? (i.vector === "plane" ? "passenger" : ""),
        vessel: i.vessel_name ?? "",
      },
    });
  }
  return { type: "FeatureCollection", features: out };
}

/** Daily-position pins. One feature per route_log entry per incident, capped
 *  at `selectedDate` so we only show waypoints the journey has reached. */
function buildRoutePinFeatureCollection(rows: Incident[], locale: "en" | "nl", selectedDate: string): GeoJSON.FeatureCollection<GeoJSON.Point> {
  const out: GeoJSON.Feature<GeoJSON.Point>[] = [];
  for (const inc of rows) {
    const allLog = parseRouteLog(inc);
    if (allLog.length < 1) continue;
    const visibleLog = allLog.filter((w) => w.date <= selectedDate);
    if (!visibleLog.length) continue;
    const lastIdx = visibleLog.length - 1;
    visibleLog.forEach((w, idx) => {
      // role:
      //   start = first waypoint of the whole journey
      //   end   = last waypoint of the whole journey
      //   head  = last waypoint we've reached so far (becomes start/end when applicable)
      //   step  = intermediate
      let role: "start" | "step" | "head" | "end" = "step";
      if (idx === 0) role = "start";
      else if (idx === lastIdx && lastIdx === allLog.length - 1) role = "end";
      else if (idx === lastIdx) role = "head";
      const vessel = loc(locale, inc.vessel_name, inc.vessel_name_nl);
      const locLabel = loc(locale, w.location, w.location_nl);
      out.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [w.lon, w.lat] },
        properties: {
          incident_id: inc.id,
          incident_vector: inc.vector ?? "",
          incident_flight_type: inc.flight_type ?? (inc.vector === "plane" ? "passenger" : ""),
          vessel_name: vessel,
          date: w.date,
          location: locLabel,
          note: loc(locale, w.note_en, w.note_nl),
          role,
        },
      });
    });
  }
  return { type: "FeatureCollection", features: out };
}

function ensureLayers(): void {
  if (!map.isStyleLoaded()) {
    map.once("idle", ensureLayers);
    return;
  }
  if (!map.getSource(SOURCE_TRAJECTORIES)) {
    map.addSource(SOURCE_TRAJECTORIES, { type: "geojson", data: emptyFc() });
  }
  if (!map.getSource(SOURCE_ROUTE_PINS)) {
    map.addSource(SOURCE_ROUTE_PINS, { type: "geojson", data: emptyFc() });
  }
  if (!map.getSource(SOURCE_INCIDENTS)) {
    map.addSource(SOURCE_INCIDENTS, { type: "geojson", data: emptyFc() });
  }
  if (!map.getSource(SOURCE_PERSONS)) {
    // Persons get clustered at low zoom: 5+ patient hospitals in the
    // Netherlands/Europe pile on top of each other on a world view, so we
    // collapse them into a single "+N" cluster bubble below clusterMaxZoom.
    map.addSource(SOURCE_PERSONS, {
      type: "geojson",
      data: emptyFc(),
      cluster: true,
      clusterRadius: 38,
      clusterMaxZoom: 5,
      // Aggregate "worst status" per cluster — deceased > ill > suspected.
      // These priorities make the cluster colour reflect the most severe
      // status in the bundle.
      clusterProperties: {
        deceased: ["+", ["case", ["==", ["get", "status"], "deceased"], 1, 0]],
        ill: ["+", ["case", ["==", ["get", "status"], "ill"], 1, 0]],
        suspected: ["+", ["case", ["==", ["get", "status"], "suspected"], 1, 0]],
      },
    });
  }

  // Trajectory lines — dashed for cruise ships, dotted for flights.
  if (!map.getLayer("trajectory-lines-cruise")) {
    map.addLayer({
      id: "trajectory-lines-cruise",
      type: "line",
      source: SOURCE_TRAJECTORIES,
      filter: ["==", ["get", "vector"], "cruise_ship"],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#4dd0e1",
        "line-width": 1.6,
        "line-opacity": 0.85,
        "line-dasharray": [3, 2],
      },
    });
  }
  if (!map.getLayer("trajectory-lines-plane")) {
    map.addLayer({
      id: "trajectory-lines-plane",
      type: "line",
      source: SOURCE_TRAJECTORIES,
      filter: ["==", ["get", "vector"], "plane"],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        // Medevac flights are supervised — render in muted grey. Scheduled
        // passenger flights that unknowingly carried an infected person stay
        // yellow ("alarm" colour).
        "line-color": [
          "case",
          ["==", ["get", "flight_type"], "medevac"], "#b6bcc6",
          "#ffb700",
        ],
        "line-width": 1.4,
        "line-opacity": 0.9,
        "line-dasharray": [1, 2.5],
      },
    });
  }
  // Daily route pins — small clickable circles, one per route_log entry.
  // Start / end pins get a distinctive cap so the user can quickly see where
  // a journey began and where it currently is.
  if (!map.getLayer("route-pins-step")) {
    map.addLayer({
      id: "route-pins-step",
      type: "circle",
      source: SOURCE_ROUTE_PINS,
      filter: ["==", ["get", "role"], "step"],
      paint: {
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          1, 2.4,
          5, 4.2,
        ],
        "circle-color": "#06080d",
        "circle-stroke-width": 1.2,
        "circle-stroke-color": [
          "case",
          ["all", ["==", ["get", "incident_vector"], "plane"], ["==", ["get", "incident_flight_type"], "medevac"]], "#b6bcc6",
          ["==", ["get", "incident_vector"], "plane"], "#ffb700",
          ["==", ["get", "incident_vector"], "cruise_ship"], "#4dd0e1",
          "#bbbbbb",
        ],
        "circle-opacity": 0.95,
      },
    });
  }
  if (!map.getLayer("route-pins-cap")) {
    map.addLayer({
      id: "route-pins-cap",
      type: "circle",
      source: SOURCE_ROUTE_PINS,
      filter: ["any",
        ["==", ["get", "role"], "start"],
        ["==", ["get", "role"], "end"],
        ["==", ["get", "role"], "head"],
      ],
      paint: {
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          1, 4,
          5, 7,
        ],
        "circle-color": [
          "case",
          ["all", ["==", ["get", "incident_vector"], "plane"], ["==", ["get", "incident_flight_type"], "medevac"]], "#b6bcc6",
          ["==", ["get", "incident_vector"], "plane"], "#ffb700",
          ["==", ["get", "incident_vector"], "cruise_ship"], "#4dd0e1",
          "#bbbbbb",
        ],
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#06080d",
        "circle-opacity": 1,
      },
    });
  }

  // Pulse halo (translucent disc behind the marker for hot-zone feel).
  if (!map.getLayer("incidents-halo")) {
    map.addLayer({
      id: "incidents-halo",
      type: "circle",
      source: SOURCE_INCIDENTS,
      paint: {
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          1, 12,
          5, 22,
        ],
        "circle-color": "#ff2a2a",
        "circle-opacity": 0.18,
        "circle-blur": 0.8,
      },
    });
  }
  // Outer ring + inner core, colour-keyed by vector / case_type.
  if (!map.getLayer("incidents-core")) {
    map.addLayer({
      id: "incidents-core",
      type: "circle",
      source: SOURCE_INCIDENTS,
      paint: {
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          1, 4,
          5, 8,
        ],
        "circle-color": [
          "case",
          ["==", ["get", "vector"], "plane"], "#ffb700",
          ["==", ["get", "vector"], "cruise_ship"], "#4dd0e1",
          ["==", ["get", "case_type"], "deceased"], "#ff0033",
          "#ff5a3a",
        ],
        "circle-stroke-color": "#06080d",
        "circle-stroke-width": 1.5,
      },
    });
  }
  // Vehicle glyphs — boat / plane SVGs loaded as map images and used as
  // symbol layer icons. They sit on top of the circle and replace the dot for
  // cruise/plane incidents.
  if (!map.getLayer("incidents-vehicle")) {
    map.addLayer({
      id: "incidents-vehicle",
      type: "symbol",
      source: SOURCE_INCIDENTS,
      filter: ["in", ["get", "vector"], ["literal", ["plane", "cruise_ship"]]],
      layout: {
        "icon-image": [
          "match", ["get", "vector"],
          "plane", "icon-plane",
          "cruise_ship", "icon-boat",
          "icon-boat",
        ],
        "icon-size": [
          "interpolate", ["linear"], ["zoom"],
          1, 0.45,
          5, 0.85,
        ],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
        "icon-color": [
          "case",
          ["all", ["==", ["get", "vector"], "plane"], ["==", ["get", "flight_type"], "medevac"]], "#b6bcc6",
          ["==", ["get", "vector"], "plane"], "#ffb700",
          "#4dd0e1",
        ],
      } as unknown as maplibregl.SymbolLayerSpecification["paint"],
    });
  }

  // Click handlers via map events — perfectly anchored to GPU-rendered features.
  map.on("click", "incidents-core", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const id = f.properties?.id as string | undefined;
    if (id) selectIncident(id);
  });
  map.on("click", "incidents-vehicle", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const id = f.properties?.id as string | undefined;
    if (id) selectIncident(id);
  });
  for (const lyr of ["incidents-core", "incidents-vehicle", "route-pins-step", "route-pins-cap", "persons-dot", "persons-hospital"]) {
    map.on("mouseenter", lyr, () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", lyr, () => { map.getCanvas().style.cursor = ""; });
  }

  // Person dot click → small popup with the patient summary.
  for (const lyr of ["persons-dot", "persons-hospital"] as const)
  map.on("click", lyr, (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const props = f.properties ?? {};
    const cc = String(props.nationality ?? "").toLowerCase();
    const flag = cc ? `<img class="route-pop__flag" src="https://flagcdn.com/24x18/${cc}.png" alt="">` : "";
    const html = `
      <div class="route-pop">
        <div class="route-pop__date">${escapeHtml(String(props.date ?? ""))}</div>
        <div class="route-pop__loc">${flag} ${escapeHtml(String(props.label ?? ""))}</div>
        <div class="route-pop__note"><strong>${escapeHtml(String(props.location ?? ""))}</strong></div>
        ${props.note ? `<div class="route-pop__note">${escapeHtml(String(props.note))}</div>` : ""}
      </div>
    `;
    openPopup(e.lngLat, html);
  });

  // Per-person markers — rendered as a hospital-square glyph at the patient's
  // current treatment location, recoloured by status:
  //   suspected (under investigation) → yellow
  //   ill (lab-confirmed alive)       → red
  //   deceased                        → black, red outline
  if (!map.getLayer("persons-halo")) {
    map.addLayer({
      id: "persons-halo",
      type: "circle",
      source: SOURCE_PERSONS,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          1, 10,
          5, 18,
          9, 30,
        ],
        "circle-color": [
          "case",
          ["==", ["get", "status"], "deceased"], "#000000",
          ["==", ["get", "status"], "suspected"], "#ffb700",
          ["==", ["get", "status"], "cleared"], "#6abe30",
          "#ff2a2a",
        ],
        "circle-opacity": 0.22,
        "circle-blur": 0.85,
      },
    });
  }
  if (!map.getLayer("persons-dot")) {
    map.addLayer({
      id: "persons-dot",
      type: "circle",
      source: SOURCE_PERSONS,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          1, 6,
          5, 11,
          9, 16,
        ],
        "circle-color": [
          "case",
          ["==", ["get", "status"], "deceased"], "#000000",
          ["==", ["get", "status"], "suspected"], "#ffb700",
          ["==", ["get", "status"], "cleared"], "#6abe30",
          "#ff2a2a",
        ],
        "circle-stroke-color": [
          "case",
          ["==", ["get", "status"], "deceased"], "#ff2a2a",
          ["==", ["get", "status"], "suspected"], "#7a4d00",
          ["==", ["get", "status"], "cleared"], "#2c4f10",
          "#06080d",
        ],
        "circle-stroke-width": [
          "interpolate", ["linear"], ["zoom"],
          1, 1.2,
          9, 2.0,
        ],
      },
    });
  }
  if (!map.getLayer("persons-hospital")) {
    map.addLayer({
      id: "persons-hospital",
      type: "symbol",
      source: SOURCE_PERSONS,
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": "icon-hospital",
        "icon-size": [
          "interpolate", ["linear"], ["zoom"],
          1, 0.18,
          5, 0.32,
          9, 0.5,
        ],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
        // Cross is white-on-coloured-circle for ill/suspected, red-on-black
        // for deceased so the cross stays visible against the dark fill.
        "icon-color": [
          "case",
          ["==", ["get", "status"], "deceased"], "#ff2a2a",
          ["==", ["get", "status"], "suspected"], "#06080d",
          "#06080d",
        ],
      } as unknown as maplibregl.SymbolLayerSpecification["paint"],
    });
  }

  // CLUSTER LAYERS — when several patient hospitals overlap at low zoom,
  // MapLibre collapses them into a single feature with a `point_count`. We
  // render that cluster as a circle whose colour follows the worst status in
  // the bundle (deceased > ill > suspected) and stamp the count inside.
  if (!map.getLayer("persons-cluster")) {
    map.addLayer({
      id: "persons-cluster",
      type: "circle",
      source: SOURCE_PERSONS,
      filter: ["has", "point_count"],
      paint: {
        "circle-radius": [
          "interpolate", ["linear"], ["get", "point_count"],
          2, 14,
          5, 18,
          10, 24,
        ],
        "circle-color": [
          "case",
          [">", ["get", "deceased"], 0], "#000000",
          [">", ["get", "ill"], 0], "#ff2a2a",
          "#ffb700",
        ],
        "circle-stroke-color": [
          "case",
          [">", ["get", "deceased"], 0], "#ff2a2a",
          "#06080d",
        ],
        "circle-stroke-width": 1.6,
        "circle-opacity": 0.92,
      },
    });
  }
  if (!map.getLayer("persons-cluster-count")) {
    map.addLayer({
      id: "persons-cluster-count",
      type: "symbol",
      source: SOURCE_PERSONS,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        // demotiles.maplibre.org/font hosts Open Sans + Arial Unicode MS only.
        // Use the first as the cluster count needs digits + plus signs.
        "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
        "text-size": 12,
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": [
          "case",
          [">", ["get", "ill"], 0], "#ffffff",
          "#06080d",
        ],
        "text-halo-color": [
          "case",
          [">", ["get", "deceased"], 0], "#ff2a2a",
          "rgba(0,0,0,0)",
        ],
        "text-halo-width": 0.6,
      },
    });
  }
  // Click on a cluster → zoom in to expand it.
  map.on("click", "persons-cluster", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const id = f.properties?.cluster_id as number | undefined;
    if (id == null) return;
    const src = map.getSource(SOURCE_PERSONS) as maplibregl.GeoJSONSource;
    src.getClusterExpansionZoom(id).then((zoom) => {
      const geom = f.geometry as GeoJSON.Point;
      map.easeTo({ center: geom.coordinates as [number, number], zoom });
    }).catch(() => { /* swallow */ });
  });
  map.on("mouseenter", "persons-cluster", () => { map.getCanvas().style.cursor = "pointer"; });
  map.on("mouseleave", "persons-cluster", () => { map.getCanvas().style.cursor = ""; });
  // Click on a daily route pin → small popup with date + location + note.
  // Click on the underlying incident card is still available via the cap pins.
  for (const lyr of ["route-pins-step", "route-pins-cap"]) {
    map.on("click", lyr, (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const props = f.properties ?? {};
      const date = String(props.date ?? "");
      const location = String(props.location ?? "");
      const note = String(props.note ?? "");
      const incidentId = String(props.incident_id ?? "");
      const html = `
        <div class="route-pop">
          <div class="route-pop__date">${escapeHtml(date)}</div>
          <div class="route-pop__loc">${escapeHtml(location)}</div>
          ${note ? `<div class="route-pop__note">${escapeHtml(note)}</div>` : ""}
          ${incidentId ? `<button class="route-pop__open" data-id="${incidentId}">${t(state.locale, "route.openIncident")}</button>` : ""}
        </div>
      `;
      const popup = openPopup(e.lngLat, html);
      // Wire up the "open incident" button.
      popup.getElement()?.querySelector<HTMLButtonElement>(".route-pop__open")?.addEventListener("click", () => {
        if (incidentId) selectIncident(incidentId);
        popup.remove();
      });
    });
  }
}

/** One Point feature per CONFIRMED person, positioned at their latest event ≤
 *  selectedDate. Returns nothing for persons whose first event hasn't happened
 *  yet — a confirmed-case dot only appears once the case exists. */
function buildPersonFeatureCollection(rows: Incident[], locale: "en" | "nl", selectedDate: string): GeoJSON.FeatureCollection<GeoJSON.Point> {
  const out: GeoJSON.Feature<GeoJSON.Point>[] = [];
  const seen = new Set<string>();
  for (const inc of rows) {
    const persons = parsePersons(inc);
    for (const p of persons) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      const all = p.events ?? [];
      // Find the latest event ≤ selectedDate.
      let curIdx = -1;
      for (let i = 0; i < all.length; i++) {
        if (all[i]!.date <= selectedDate) curIdx = i;
      }
      if (curIdx < 0) continue;
      const cur = all[curIdx]!;
      // During playback we interpolate toward the next event if the next event
      // is on the very next day — otherwise jumps would land mid-ocean.
      let lon = cur.lon, lat = cur.lat;
      const nxt = curIdx + 1 < all.length ? all[curIdx + 1]! : null;
      if (nxt && interp > 0) {
        const dayMs = 86_400_000;
        const gap = (Date.parse(nxt.date) - Date.parse(cur.date)) / dayMs;
        if (gap <= 1) {
          const k = Math.min(1, interp);
          lon = cur.lon + (nxt.lon - cur.lon) * k;
          lat = cur.lat + (nxt.lat - cur.lat) * k;
        }
      }
      const label = locale === "nl" ? p.label_nl : p.label_en;
      out.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lon, lat] },
        properties: {
          id: p.id,
          incident_id: inc.id,
          status: cur.status,
          nationality: p.nationality,
          label,
          location: loc(locale, cur.location, cur.location_nl),
          date: cur.date,
          note: loc(locale, cur.note_en, cur.note_nl),
        },
      });
    }
  }
  return { type: "FeatureCollection", features: out };
}

function refreshMapData(): void {
  const inc = map.getSource(SOURCE_INCIDENTS) as maplibregl.GeoJSONSource | undefined;
  inc?.setData(buildIncidentFeatureCollection(state.visibleIncidents));
  const traj = map.getSource(SOURCE_TRAJECTORIES) as maplibregl.GeoJSONSource | undefined;
  traj?.setData(buildTrajectoryFeatureCollection(state.visibleIncidents, state.selectedDate));
  const pins = map.getSource(SOURCE_ROUTE_PINS) as maplibregl.GeoJSONSource | undefined;
  pins?.setData(buildRoutePinFeatureCollection(state.visibleIncidents, state.locale, state.selectedDate));
  const persons = map.getSource(SOURCE_PERSONS) as maplibregl.GeoJSONSource | undefined;
  persons?.setData(buildPersonFeatureCollection(state.visibleIncidents, state.locale, state.selectedDate));
}

/** Rasterise an SVG URL to an ImageBitmap and register it as a named map
 *  image. MapLibre's `loadImage` can't decode SVGs directly — it expects
 *  raster formats — so we draw the SVG onto a canvas at a known size and feed
 *  the resulting bitmap to `addImage`. The {sdf:true} flag lets us recolour
 *  the glyph at runtime via the symbol layer's `icon-color` paint property. */
async function loadSvgImage(name: string, url: string, size = 64): Promise<void> {
  if (map.hasImage(name)) return;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`fetch ${url}: ${r.status}`);
  let svgText = await r.text();
  // Force a white fill (SDF only cares about alpha, but Chromium refuses to
  // decode SVGs via Image when fill="currentColor" with no actual colour).
  svgText = svgText.replace(/currentColor/g, "#ffffff");
  // Make sure the SVG declares its own width/height — some browsers fail to
  // decode if neither is present.
  if (!/width=/.test(svgText)) {
    svgText = svgText.replace("<svg", `<svg width="${size}" height="${size}"`);
  }
  const dataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgText);

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = (e) => reject(new Error("image load failed: " + String(e)));
    i.src = dataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d ctx unavailable");
  ctx.drawImage(img, 0, 0, size, size);
  const data = ctx.getImageData(0, 0, size, size);
  map.addImage(name, data, { sdf: true });
}

/** Load boat + plane + hospital SVGs into the map style as named images. */
async function loadVehicleIcons(): Promise<void> {
  await Promise.all([
    loadSvgImage("icon-boat", "/icons/boat.svg").catch((e) => console.error("[icon] boat", e)),
    loadSvgImage("icon-plane", "/icons/plane.svg").catch((e) => console.error("[icon] plane", e)),
    loadSvgImage("icon-hospital", "/icons/hospital.svg", 64).catch((e) => console.error("[icon] hospital", e)),
  ]);
}

// Tint country fills red where there's at least one active case (Plague-Inc.-style hot zones).
//
// The country GeoJSON dataset (datasets/geo-countries) exposes ISO codes under
// "ISO3166-1-Alpha-2" — earlier versions of this code looked for "ISO_A2" and
// silently matched zero countries, which is why no highlight ever fired.
function applyHotZones(): void {
  if (!map.isStyleLoaded()) {
    map.once("idle", applyHotZones);
    return;
  }
  // A country lights up if a visible incident is anchored there, OR a
  // confirmed-case person is at a location in that country on the selected
  // date. The latter is what makes Switzerland glow once the Swiss patient is
  // confirmed in Zurich, even though the parent Hondius incident is anchored
  // at Cape Verde.
  const hot = new Set<string>();
  for (const inc of state.visibleIncidents) {
    if (inc.country_code) hot.add(inc.country_code);
    for (const p of parsePersons(inc)) {
      const evts = (p.events ?? []).filter((e) => e.date <= state.selectedDate);
      const cur = evts[evts.length - 1];
      if (cur?.country_code) hot.add(cur.country_code);
    }
  }
  const filter: maplibregl.FilterSpecification = hot.size
    ? ["in", ["get", "ISO3166-1-Alpha-2"], ["literal", [...hot]]]
    : ["==", ["get", "ISO3166-1-Alpha-2"], ""];
  if (map.getLayer("land-highlight")) map.setFilter("land-highlight", filter);
  if (map.getLayer("land-highlight-glow")) map.setFilter("land-highlight-glow", filter);
}

// ────────────────────────────────────────────────────────────────────
// Filters / visible-set computation
// ────────────────────────────────────────────────────────────────────
function recomputeVisible(): void {
  const f = state.filters;
  const strainsActive = f.strains.size > 0;
  const countriesActive = f.countries.size > 0;
  state.visibleIncidents = state.allIncidents.filter((i) => {
    // Visibility rule: an incident becomes visible the first day a related
    // event happens — that's either its earliest route_log waypoint (e.g. the
    // ship's departure from Argentina) or its `reported_at` date if there's
    // no route_log. The vessel's symbol moves along the daily waypoints, but
    // the counters (cases / deaths) stay gated on reported_at downstream.
    const log = parseRouteLog(i);
    const earliest = log.length ? log[0]!.date : i.reported_at;
    if (earliest > state.selectedDate) return false;
    if (strainsActive && !f.strains.has(i.strain ?? "")) return false;
    if (countriesActive && !f.countries.has(i.country_code ?? "")) return false;
    if (!i.vector && !f.vectors.none) return false;
    if (i.vector === "cruise_ship" && !f.vectors.cruise_ship) return false;
    if (i.vector === "plane" && !f.vectors.plane) return false;
    return true;
  });
  refreshMapData();
  applyHotZones();
  renderCounters();
  if (state.selectedIncidentId &&
      !state.visibleIncidents.some(i => i.id === state.selectedIncidentId)) {
    closeDetailPanel();
  }
}

// ────────────────────────────────────────────────────────────────────
// Top bar — counters + date
// ────────────────────────────────────────────────────────────────────
/** Cumulative case totals for an incident up to `selectedDate`.
 *  When the route_log has explicit per-day deltas we sum those (so counters
 *  rise day-by-day as deaths happen). Otherwise we fall back to the static
 *  case_count once `reported_at` has been reached. */
function dynamicTotals(inc: Incident, selectedDate: string): { detected: number; deceased: number } {
  const log = parseRouteLog(inc);
  const hasDeltas = log.some((w) => w.delta);
  if (hasDeltas) {
    let detected = 0, deceased = 0;
    for (const w of log) {
      if (w.date > selectedDate) break;
      detected += w.delta?.detected ?? 0;
      deceased += w.delta?.deceased ?? 0;
    }
    return { detected, deceased };
  }
  if (inc.reported_at > selectedDate) return { detected: 0, deceased: 0 };
  return inc.case_type === "deceased"
    ? { detected: 0, deceased: inc.case_count }
    : { detected: inc.case_count, deceased: 0 };
}

function renderCounters(): void {
  let cases = 0, deceased = 0;
  const countries = new Set<string>();
  for (const i of state.visibleIncidents) {
    const t = dynamicTotals(i, state.selectedDate);
    cases += t.detected + t.deceased;
    deceased += t.deceased;
    if (i.country_code && (t.detected + t.deceased > 0)) countries.add(i.country_code);
    // Confirmed-person locations also count — the Swiss patient is in CH even
    // though their parent incident's country_code is CV.
    for (const p of parsePersons(i)) {
      const evts = (p.events ?? []).filter((e) => e.date <= state.selectedDate);
      const cur = evts[evts.length - 1];
      if (cur?.country_code) countries.add(cur.country_code);
    }
  }
  byId("counter-cases").textContent = cases.toLocaleString(state.locale);
  byId("counter-deceased").textContent = deceased.toLocaleString(state.locale);
  byId("counter-countries").textContent = String(countries.size);
}

function renderDayReadout(): void {
  if (!state.dates.length) return;
  const first = state.dates[0]!.date;
  const dayNum = Math.floor(
    (Date.parse(state.selectedDate) - Date.parse(first)) / (1000 * 60 * 60 * 24),
  ) + 1;
  byId("day-num").textContent = dayNum.toLocaleString();
  byId("day-iso").textContent = state.selectedDate;
  byId("timeline-current").textContent = state.selectedDate;
  renderTicker();
}

/** Build the daily-brief ticker for `state.selectedDate`. We pull every note
 *  attached to that date — ship route_log entries + per-person events — across
 *  all visible incidents, then drive a typewriter that types out one item at
 *  a time. Click the active line to open the full DAY DIGEST modal. */

type TickerItem = { kind: "ship" | "person"; note: string; location: string };

let typewriterTimer: number | null = null;
let typewriterAdvanceTimer: number | null = null;
let activeItemIdx = 0;
let cachedItems: TickerItem[] = [];

function gatherTickerItems(date: string): TickerItem[] {
  const items: TickerItem[] = [];
  for (const inc of state.visibleIncidents) {
    for (const w of parseRouteLog(inc)) {
      if (w.date !== date) continue;
      const note = loc(state.locale, w.note_en, w.note_nl);
      if (note) items.push({ kind: "ship", note, location: loc(state.locale, w.location, w.location_nl) });
    }
    for (const p of parsePersons(inc)) {
      for (const e of p.events ?? []) {
        if (e.date !== date) continue;
        const label = state.locale === "nl" ? p.label_nl : p.label_en;
        const note = loc(state.locale, e.note_en, e.note_nl);
        items.push({ kind: "person", note: note || label, location: loc(state.locale, e.location, e.location_nl) });
      }
    }
  }
  return items;
}

function clearTypewriterTimers(): void {
  if (typewriterTimer !== null) { window.clearTimeout(typewriterTimer); typewriterTimer = null; }
  if (typewriterAdvanceTimer !== null) { window.clearTimeout(typewriterAdvanceTimer); typewriterAdvanceTimer = null; }
}

function renderTicker(): void {
  const date = state.selectedDate;
  byId("ticker-date").textContent = date;

  cachedItems = gatherTickerItems(date);
  activeItemIdx = 0;
  clearTypewriterTimers();

  const typeBtn = byId<HTMLButtonElement>("ticker-type");
  const text = byId<HTMLSpanElement>("ticker-type-text");
  const count = byId("ticker-count");
  const prev = byId<HTMLButtonElement>("ticker-prev");
  const next = byId<HTMLButtonElement>("ticker-next");

  if (!cachedItems.length) {
    typeBtn.classList.add("ticker__type--empty");
    typeBtn.disabled = true;
    text.textContent = t(state.locale, "ticker.empty");
    count.textContent = "0/0";
    prev.disabled = true; next.disabled = true;
    return;
  }
  typeBtn.classList.remove("ticker__type--empty");
  typeBtn.disabled = false;
  startTypewriterForActive();
}

/** Type out the current active item character by character. When done, if
 *  there are more items, schedule a switch to the next one. When playing,
 *  the day will advance independently — but we still cycle items so users see
 *  every headline at least once. */
function startTypewriterForActive(): void {
  clearTypewriterTimers();
  const text = byId<HTMLSpanElement>("ticker-type-text");
  const count = byId("ticker-count");
  const prev = byId<HTMLButtonElement>("ticker-prev");
  const next = byId<HTMLButtonElement>("ticker-next");

  if (!cachedItems.length) return;
  if (activeItemIdx >= cachedItems.length) activeItemIdx = 0;
  const item = cachedItems[activeItemIdx]!;
  count.textContent = `${activeItemIdx + 1}/${cachedItems.length}`;
  prev.disabled = cachedItems.length < 2;
  next.disabled = cachedItems.length < 2;

  // Build the full headline: LOCATION — note (uppercase).
  const head = item.location ? `${item.location} — ` : "";
  const full = (head + item.note).toUpperCase();

  // Decide per-character delay based on play state. While playing we type a
  // bit faster so the headline lands within the day's interval.
  const charDelay = state.playing
    ? Math.max(20, Math.min(70, (state.PLAY_BASE_MS / state.speed - 1500) / Math.max(1, full.length)))
    : 38;

  let cursor = 0;
  text.textContent = "";

  const tick = () => {
    if (cursor < full.length) {
      cursor++;
      text.textContent = full.slice(0, cursor);
      typewriterTimer = window.setTimeout(tick, charDelay);
    } else {
      // Finished typing this item. If multiple items, queue the next one.
      if (cachedItems.length > 1) {
        const pause = state.playing ? 1100 : 2500;
        typewriterAdvanceTimer = window.setTimeout(() => {
          activeItemIdx = (activeItemIdx + 1) % cachedItems.length;
          startTypewriterForActive();
        }, pause);
      }
    }
  };
  tick();
}

function openDigest(): void {
  if (!cachedItems.length) return;
  byId("digest-date").textContent = state.selectedDate;
  const list = byId("digest-list");
  list.innerHTML = cachedItems.map((it) => `
    <li class="digest__item">
      <span class="digest__bullet ${it.kind === "ship" ? "digest__bullet--ship" : ""}"></span>
      <div class="digest__loc">${escapeHtml(it.location || "")}</div>
      <div class="digest__note">${escapeHtml(it.note)}</div>
    </li>
  `).join("");
  const dlg = byId("digest");
  dlg.hidden = false;
  dlg.setAttribute("aria-hidden", "false");
}

function closeDigest(): void {
  const dlg = byId("digest");
  dlg.hidden = true;
  dlg.setAttribute("aria-hidden", "true");
}

// ────────────────────────────────────────────────────────────────────
// Filters rail rendering
// ────────────────────────────────────────────────────────────────────
function renderFilters(): void {
  // Strains — keyed on the English value (stable across locales) but displayed
  // in the active locale.
  const strainCounts = new Map<string, number>();
  const strainLabel = new Map<string, string>();
  for (const i of state.allIncidents) {
    const k = i.strain ?? "(unknown)";
    strainCounts.set(k, (strainCounts.get(k) ?? 0) + 1);
    strainLabel.set(k, loc(state.locale, i.strain, i.strain_nl) || k);
  }
  const strainList = byId("filter-strains");
  strainList.innerHTML = "";
  for (const [strain, count] of [...strainCounts.entries()].sort((a, b) => b[1] - a[1])) {
    const lbl = document.createElement("label");
    lbl.className = "check";
    lbl.innerHTML = `
      <input type="checkbox" data-strain="${strain}" />
      <span class="check__label">${escapeHtml(strainLabel.get(strain) ?? strain)}</span>
      <span class="check__count">${count}</span>
    `;
    const cb = lbl.querySelector<HTMLInputElement>("input")!;
    cb.checked = state.filters.strains.size === 0 || state.filters.strains.has(strain);
    cb.addEventListener("change", () => {
      if (state.filters.strains.size === 0) {
        // First user click — switch from "all" to explicit set.
        for (const s of strainCounts.keys()) state.filters.strains.add(s);
      }
      if (cb.checked) state.filters.strains.add(strain);
      else state.filters.strains.delete(strain);
      // If all strains end up checked, collapse back to "all".
      if (state.filters.strains.size === strainCounts.size) state.filters.strains.clear();
      recomputeVisible();
    });
    strainList.appendChild(lbl);
  }

  // Countries involved — union of every incident.country_code + every entry in
  // each incident's countries_visited list. The countries_visited list lets a
  // single cruise show all eight territories its route touches, not just the
  // country where the case is anchored.
  type CountryEntry = { code: string; label_en: string; label_nl: string; count: number };
  const countryMap = new Map<string, CountryEntry>();
  /** Increment a country, optionally upgrading the labels when a richer one
   *  arrives. The first call may pass the raw ISO code as label (when a value
   *  is taken from `incident.country_code`); a later call from
   *  `countries_visited` brings real names — let those take precedence. */
  function bumpCountry(code: string, label_en: string, label_nl: string): void {
    const cur = countryMap.get(code);
    if (cur) {
      cur.count++;
      if (label_en && label_en !== code && cur.label_en === code) cur.label_en = label_en;
      if (label_nl && label_nl !== code && cur.label_nl === code) cur.label_nl = label_nl;
      return;
    }
    countryMap.set(code, { code, label_en, label_nl, count: 1 });
  }
  for (const i of state.allIncidents) {
    if (i.country_code) bumpCountry(i.country_code, i.country_code, i.country_code);
    for (const c of parseCountriesVisited(i)) bumpCountry(c.code, c.label_en, c.label_nl);
    // Confirmed-person locations: e.g. the Swiss patient surfaces "CH" even
    // though the parent Hondius incident's country_code is "CV".
    for (const p of parsePersons(i)) {
      for (const e of p.events ?? []) {
        if (e.country_code) bumpCountry(e.country_code, e.country_code, e.country_code);
      }
    }
  }
  const countryList = byId("filter-countries");
  countryList.innerHTML = "";
  for (const c of [...countryMap.values()].sort((a, b) => b.count - a.count)) {
    const lbl = document.createElement("label");
    lbl.className = "check check--country";
    const cc = c.code.toLowerCase();
    const label = state.locale === "nl" ? c.label_nl : c.label_en;
    const flagUrl = `https://flagcdn.com/24x18/${cc}.png`;
    lbl.innerHTML = `
      <input type="checkbox" data-country="${c.code}" />
      <span class="check__flag" aria-hidden="true" data-cc="${escapeHtml(c.code)}">
        <img src="${flagUrl}" srcset="https://flagcdn.com/48x36/${cc}.png 2x" alt=""
             loading="lazy"
             onerror="this.style.display='none';this.parentElement.classList.add('check__flag--missing');" />
      </span>
      <span class="check__label">${escapeHtml(label)}</span>
    `;
    const cb = lbl.querySelector<HTMLInputElement>("input")!;
    cb.checked = state.filters.countries.size === 0 || state.filters.countries.has(c.code);
    cb.addEventListener("change", () => {
      if (state.filters.countries.size === 0) {
        for (const k of countryMap.keys()) state.filters.countries.add(k);
      }
      if (cb.checked) state.filters.countries.add(c.code);
      else state.filters.countries.delete(c.code);
      if (state.filters.countries.size === countryMap.size) state.filters.countries.clear();
      recomputeVisible();
    });
    countryList.appendChild(lbl);
  }

  // Nationalities affected — flag rows derived from confirmed persons.
  const natList = byId("filter-nationalities");
  natList.innerHTML = "";
  const natCounts = new Map<string, { count: number; label_en: string; label_nl: string }>();
  // Demonyms keyed by ISO 3166-1 alpha-2. Adding more here is safer than
  // showing a raw country code in the rail when a new nationality surfaces in
  // the data.
  const natLabels: Record<string, { en: string; nl: string }> = {
    NL: { en: "Dutch", nl: "Nederlands" },
    GB: { en: "British", nl: "Brits" },
    DE: { en: "German", nl: "Duits" },
    CH: { en: "Swiss", nl: "Zwitsers" },
    FR: { en: "French", nl: "Frans" },
    BE: { en: "Belgian", nl: "Belgisch" },
    US: { en: "American", nl: "Amerikaans" },
    ZA: { en: "South African", nl: "Zuid-Afrikaans" },
    SG: { en: "Singaporean", nl: "Singaporees" },
    IT: { en: "Italian", nl: "Italiaans" },
    ES: { en: "Spanish", nl: "Spaans" },
    PT: { en: "Portuguese", nl: "Portugees" },
    AT: { en: "Austrian", nl: "Oostenrijks" },
    SE: { en: "Swedish", nl: "Zweeds" },
    NO: { en: "Norwegian", nl: "Noors" },
    DK: { en: "Danish", nl: "Deens" },
    CA: { en: "Canadian", nl: "Canadees" },
    AU: { en: "Australian", nl: "Australisch" },
    NZ: { en: "New Zealander", nl: "Nieuw-Zeelands" },
    IE: { en: "Irish", nl: "Iers" },
    AR: { en: "Argentine", nl: "Argentijns" },
    BR: { en: "Brazilian", nl: "Braziliaans" },
    CL: { en: "Chilean", nl: "Chileens" },
    JP: { en: "Japanese", nl: "Japans" },
    CN: { en: "Chinese", nl: "Chinees" },
  };
  for (const i of state.allIncidents) {
    for (const p of parsePersons(i)) {
      const cc = p.nationality;
      // Skip privacy-protected entries (empty nationality) — they shouldn't
      // appear as a rail row; they still get a hospital marker on the map.
      if (!cc) continue;
      const lab = natLabels[cc] ?? { en: cc, nl: cc };
      const cur = natCounts.get(cc);
      if (cur) cur.count++;
      else natCounts.set(cc, { count: 1, label_en: lab.en, label_nl: lab.nl });
    }
  }
  for (const [cc, e] of [...natCounts.entries()].sort((a, b) => b[1].count - a[1].count)) {
    const row = document.createElement("div");
    row.className = "nat-row";
    const flagCc = cc.toLowerCase();
    const label = state.locale === "nl" ? e.label_nl : e.label_en;
    row.innerHTML = `
      <span class="check__flag" aria-hidden="true" data-cc="${escapeHtml(cc)}">
        <img src="https://flagcdn.com/24x18/${flagCc}.png"
             srcset="https://flagcdn.com/48x36/${flagCc}.png 2x" alt="" loading="lazy"
             onerror="this.style.display='none';this.parentElement.classList.add('check__flag--missing');" />
      </span>
      <span class="nat-row__label">${escapeHtml(label)}</span>
      <span class="check__count">${e.count}</span>
    `;
    natList.appendChild(row);
  }
}

// ────────────────────────────────────────────────────────────────────
// Right detail panel
// ────────────────────────────────────────────────────────────────────
function selectIncident(id: string): void {
  state.selectedIncidentId = id;
  const inc = state.allIncidents.find((i) => i.id === id);
  if (!inc) return;
  document.body.classList.add("panel-open");
  const panel = byId("panel-right");
  panel.hidden = false;
  byId("panel-body").innerHTML = renderDetailHTML(inc);
  // Re-apply data-i18n for the freshly built fragment.
  applyI18n(state.locale, panel);
  map.flyTo({ center: [inc.lon, inc.lat], zoom: Math.max(map.getZoom(), 3.4), speed: 0.7 });
}

function closeDetailPanel(): void {
  state.selectedIncidentId = null;
  document.body.classList.remove("panel-open");
  byId("panel-right").hidden = true;
}

function renderDetailHTML(inc: Incident): string {
  const summary = state.locale === "nl" ? inc.summary_nl : inc.summary_en;
  const typeLabel = inc.case_type === "deceased"
    ? t(state.locale, "caseType.deceased")
    : t(state.locale, "caseType.detected");
  const typeClass = inc.case_type === "deceased" ? "" : "detail__type--detected";
  const vesselLabel = loc(state.locale, inc.vessel_name, inc.vessel_name_nl);
  const locationName = loc(state.locale, inc.location_name, inc.location_name_nl);
  const strainLabel = loc(state.locale, inc.strain, inc.strain_nl) || "Hantavirus";

  const vector = inc.vector === "cruise_ship"
    ? `<div class="detail__vector detail__vector--ship">${BOAT_SVG} ${t(state.locale, "vector.cruise")}${vesselLabel ? ` · ${escapeHtml(vesselLabel)}` : ""}</div>`
    : inc.vector === "plane"
      ? `<div class="detail__vector">${PLANE_SVG} ${t(state.locale, "vector.plane")}${vesselLabel ? ` · ${escapeHtml(vesselLabel)}` : ""}</div>`
      : "";

  const sources = inc.sources.map((s) => `
    <a class="detail__source" href="${escapeAttr(s.url)}" target="_blank" rel="noopener">
      <div class="detail__source-pub">${escapeHtml(s.publisher)}</div>
      <div>${escapeHtml(s.title ?? s.url)}</div>
    </a>
  `).join("");

  return `
    <span class="detail__type ${typeClass}">${typeLabel}</span>
    <h2 class="detail__location">${escapeHtml(locationName)}</h2>
    <div class="detail__coords">
      <span>${t(state.locale, "detail.coordsLabel")}</span> ${inc.lat.toFixed(3)}, ${inc.lon.toFixed(3)}
      ${inc.country_code ? ` · ${inc.country_code}` : ""}
    </div>

    ${vector}

    <div class="detail__strain">${escapeHtml(strainLabel)}</div>

    <div class="detail__stats">
      <div>
        <div class="detail__stat-label">${t(state.locale, "detail.casesLabel")}</div>
        <div class="detail__stat-value">${inc.case_count}</div>
      </div>
      <div>
        <div class="detail__stat-label">${t(state.locale, "detail.reportedLabel")}</div>
        <div class="detail__stat-value detail__stat-value--accent">${inc.reported_at}</div>
      </div>
    </div>

    <p class="detail__summary">${escapeHtml(summary)}</p>

    <h3 class="detail__sources-title">${t(state.locale, "detail.sources")}</h3>
    ${sources || `<p class="detail__source-pub" style="color:var(--bone-2)">—</p>`}
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
function escapeAttr(s: string): string { return escapeHtml(s); }

// ────────────────────────────────────────────────────────────────────
// Timeline scrubber
// ────────────────────────────────────────────────────────────────────
function setupTimeline(): void {
  const range = byId<HTMLInputElement>("timeline-range");
  const ticks = byId("timeline-ticks");
  ticks.innerHTML = "";

  range.min = "0";
  range.max = String(Math.max(state.dates.length - 1, 0));
  range.step = "1";
  range.value = String(state.dates.length - 1);

  // Render tick marks for distinct dates.
  for (let i = 0; i < state.dates.length; i++) {
    const tick = document.createElement("div");
    tick.className = "timeline__tick";
    const pct = state.dates.length === 1 ? 0 : (i / (state.dates.length - 1)) * 100;
    tick.style.left = `${pct}%`;
    ticks.appendChild(tick);
  }

  byId("timeline-min").textContent = state.dates[0]?.date ?? "—";
  byId("timeline-max").textContent = state.dates[state.dates.length - 1]?.date ?? "—";

  range.addEventListener("input", () => {
    const idx = Number(range.value);
    state.selectedDate = state.dates[idx]?.date ?? state.selectedDate;
    // Order matters: recompute the visible-incident set FIRST, otherwise the
    // ticker (called from renderDayReadout) builds its items from the previous
    // day's state and falsely reports "no events". When playing day-by-day
    // the previous-state happened to be a near-match so the bug masked itself.
    recomputeVisible();
    renderDayReadout();
  });

  // Play / pause
  //
  // 1× is intentionally slow (≈9 s/day) so the daily news ticker has time to
  // sweep across the viewport before the day advances. 2× and 4× are
  // proportional. The ticker's own animation duration also adapts to the
  // current play interval (see renderTicker), so a sweep completes just before
  // the next day fires.
  let playInterval: number | null = null;
  let playRaf: number | null = null;
  let dayStartedAt = 0;
  const playBtn = byId("timeline-play");
  function dayDurationMs(): number {
    return Math.round(state.PLAY_BASE_MS / state.speed);
  }
  function stopPlay() {
    if (playInterval) { clearInterval(playInterval); playInterval = null; }
    if (playRaf !== null) { cancelAnimationFrame(playRaf); playRaf = null; }
    state.playing = false;
    interp = 0;
    refreshMapData();
    playBtn.classList.remove("is-playing");
    renderTicker();
  }
  function smoothFrame(now: number) {
    const t = Math.min(1, (now - dayStartedAt) / dayDurationMs());
    interp = t;
    refreshMapData();   // updates incident + person dot positions only — cheap
    if (state.playing) playRaf = requestAnimationFrame(smoothFrame);
  }
  function startPlay() {
    if (playInterval) return;
    state.playing = true;
    playBtn.classList.add("is-playing");
    renderTicker();
    dayStartedAt = performance.now();
    interp = 0;
    playRaf = requestAnimationFrame(smoothFrame);
    playInterval = window.setInterval(() => {
      let idx = Number(range.value);
      idx = idx + 1;
      if (idx > Number(range.max)) { stopPlay(); return; }
      range.value = String(idx);
      range.dispatchEvent(new Event("input"));
      // New day starts now — reset interp clock so smoothing begins fresh.
      dayStartedAt = performance.now();
      interp = 0;
    }, dayDurationMs());
  }
  playBtn.addEventListener("click", () => state.playing ? stopPlay() : startPlay());

  // Speed buttons
  document.querySelectorAll<HTMLButtonElement>(".timeline__speeds .speed").forEach((b) => {
    b.addEventListener("click", () => {
      document.querySelectorAll(".timeline__speeds .speed").forEach((x) => x.classList.remove("speed--active"));
      b.classList.add("speed--active");
      state.speed = Number(b.dataset.speed) as 1 | 2 | 4;
      if (state.playing) { stopPlay(); startPlay(); }
    });
  });
}

// ────────────────────────────────────────────────────────────────────
// Vector + filter wiring
// ────────────────────────────────────────────────────────────────────
function setupRail(): void {
  document.querySelectorAll<HTMLInputElement>("[data-vector]").forEach((cb) => {
    cb.addEventListener("change", () => {
      const v = cb.dataset.vector as keyof State["filters"]["vectors"];
      state.filters.vectors[v] = cb.checked;
      recomputeVisible();
    });
  });

  byId("rail-collapse").addEventListener("click", () => {
    document.body.classList.toggle("rail-collapsed");
    byId("rail-left").classList.toggle("rail--collapsed");
  });

  byId("panel-close").addEventListener("click", closeDetailPanel);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      closeDetailPanel();
      closeDigest();
    }
  });

  byId("fit-btn").addEventListener("click", fitToVisible);
  byId("topbar-brand").addEventListener("click", fitToVisible);

  // Ticker — typewriter click opens the day digest, prev/next cycles items.
  byId("ticker-type").addEventListener("click", openDigest);
  byId("ticker-prev").addEventListener("click", () => {
    if (cachedItems.length < 2) return;
    activeItemIdx = (activeItemIdx - 1 + cachedItems.length) % cachedItems.length;
    startTypewriterForActive();
  });
  byId("ticker-next").addEventListener("click", () => {
    if (cachedItems.length < 2) return;
    activeItemIdx = (activeItemIdx + 1) % cachedItems.length;
    startTypewriterForActive();
  });
  byId("digest-close").addEventListener("click", closeDigest);
  byId("digest").addEventListener("click", (e) => {
    if (e.target === byId("digest")) closeDigest();
  });

  // Legend toggle (mirrored across desktop button + mobile FAB)
  const legendEl = byId("legend");
  const legendBtn = byId("legend-btn");
  function toggleLegend(force?: boolean): void {
    const open = force !== undefined ? force : legendEl.hidden;
    legendEl.hidden = !open;
    legendBtn.classList.toggle("is-active", open);
    document.getElementById("fab-legend")?.classList.toggle("is-active", open);
  }
  legendBtn.addEventListener("click", () => toggleLegend());
  byId("legend-close").addEventListener("click", () => toggleLegend(false));

  // Mobile FAB cluster
  byId("fab-filters").addEventListener("click", () => {
    document.body.classList.toggle("rail-mobile-open");
    document.getElementById("fab-filters")?.classList.toggle(
      "is-active",
      document.body.classList.contains("rail-mobile-open"),
    );
  });
  byId("fab-digest").addEventListener("click", openDigest);
  byId("fab-fit").addEventListener("click", fitToVisible);
  byId("fab-legend")?.addEventListener("click", () => toggleLegend());
  byId("fab-lang").addEventListener("click", () => byId("lang-toggle").click());
  // Tap outside the rail (on the map) closes the mobile rail sheet.
  byId("map").addEventListener("click", () => {
    document.body.classList.remove("rail-mobile-open");
  });
}

// ────────────────────────────────────────────────────────────────────
// Lang toggle
// ────────────────────────────────────────────────────────────────────
function setupLangToggle(): void {
  const opts = document.querySelectorAll<HTMLElement>(".lang-toggle__opt");
  const fabLangText = document.getElementById("fab-lang-text");
  function syncActive() {
    opts.forEach((o) => o.classList.toggle("active", o.dataset.lang === state.locale));
    if (fabLangText) fabLangText.textContent = state.locale.toUpperCase();
  }
  syncActive();
  byId("lang-toggle").addEventListener("click", () => {
    state.locale = state.locale === "en" ? "nl" : "en";
    setLocale(state.locale);
    applyI18n(state.locale);
    syncActive();
    if (state.selectedIncidentId) selectIncident(state.selectedIncidentId);
    renderCounters();
    // Route pin tooltips + ticker copy are locale-specific — re-render so
    // future popups read in the new language.
    refreshMapData();
    renderTicker();
    // Re-render the rail filter labels (countries / nationalities are
    // localised too).
    renderFilters();
  });
}

// ────────────────────────────────────────────────────────────────────
// Boot
// ────────────────────────────────────────────────────────────────────
function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} missing`);
  return el as T;
}

async function boot(): Promise<void> {
  applyI18n(state.locale);
  setupLangToggle();
  setupRail();

  const [dates, incidents] = await Promise.all([fetchDates(), fetchIncidents()]);
  state.dates = dates;
  state.allIncidents = incidents;
  state.selectedDate = dates[dates.length - 1]?.date ?? new Date().toISOString().slice(0, 10);

  // Wait for the style to be ready before adding sources/layers.
  await new Promise<void>((resolve) => {
    if (map.isStyleLoaded()) resolve();
    else map.once("load", () => resolve());
  });
  await loadVehicleIcons();
  ensureLayers();

  renderFilters();
  setupTimeline();
  // Visible-set first, readout (which feeds the ticker) second.
  recomputeVisible();
  renderDayReadout();

  byId("last-update").textContent =
    `${t(state.locale, "lastSync")} ${new Date().toLocaleString(state.locale)}`;

  // Reveal: remove .boot once everything's wired so the staggered animation runs predictably.
  requestAnimationFrame(() => document.body.classList.remove("boot"));
}

boot().catch((err) => {
  console.error(err);
  byId("counter-cases").textContent = "ERR";
});
